import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_TINYCMS_DOMAINS,
  ADD_TINYCMS_DOMAIN,
  CHECK_TINYCMS_DOMAIN,
  SET_PRIMARY_TINYCMS_DOMAIN,
  REMOVE_TINYCMS_DOMAIN,
} from '../../graphql';
import { useAccount } from '../../context/AccountContext';
import { useCopy } from '../../lib/copyToClipboard';
import { SETTINGS_HISTORY_QUERY } from '../settings/SettingsHistory';
import { Loader } from '../common/PageLoader';
import { askInk } from './InkConfirm';
import { showInkToast } from './InkToast';

/**
 * Manage › Site › Domain ("TinyCMS UX optimization review", Domain): connect a
 * domain the person owns in four steps — Domain, Add records, Check, Live.
 *
 *   1 Domain       the address readers will visit (www.example.com); a bare
 *                  example.com gets "Use www.example.com", since a root domain
 *                  can't point at a website yet (domains.js addDomain).
 *   2 Add records  the CNAME and TXT, named as the provider's form asks for
 *                  them ("www"), with where to find DNS at the provider the
 *                  server detected from the name servers (or the one picked).
 *   3 Check        each record found or not, then https. The engine re-checks
 *                  every 5 minutes and the server emails when it's live, so the
 *                  page can be closed.
 *   4 Live         make it the main address (the first live domain already is).
 *
 * Not here yet: the design's "Connect with GoDaddy" (the provider adding the
 * records for you, Domain Connect). That's a separate feature; until then the
 * records are added by hand.
 */

interface DnsRecord {
  type: 'CNAME' | 'TXT' | string;
  name: string;
  host?: string;
  value: string;
  ok: boolean | null;
}
interface Domain {
  id: string;
  hostname: string;
  root?: string;
  provider?: ProviderKey | null;
  status: 'pending' | 'verifying' | 'active' | 'failed';
  isPrimary: boolean;
  paused?: boolean;
  lastError?: string | null;
  lastCheckedAt?: string | null;
  edgeConfigured?: boolean;
  records: DnsRecord[];
}

type ProviderKey = 'godaddy' | 'cloudflare' | 'squarespace' | 'namecheap' | 'ionos' | 'other';

/** Where DNS lives at each provider, in their own words, and a way in. */
const PROVIDERS: Record<ProviderKey, { name: string; where: string; url: string | null }> = {
  godaddy: {
    name: 'GoDaddy',
    where: 'In GoDaddy: My Products › your domain › DNS.',
    url: 'https://dcc.godaddy.com/control/portfolio',
  },
  cloudflare: {
    name: 'Cloudflare',
    where:
      'In Cloudflare: pick the domain › DNS › Records. Set the CNAME to “DNS only” (grey cloud).',
    url: 'https://dash.cloudflare.com/',
  },
  squarespace: {
    name: 'Squarespace Domains',
    where: 'In Squarespace: Domains › your domain › DNS settings.',
    url: 'https://account.squarespace.com/domains',
  },
  namecheap: {
    name: 'Namecheap',
    where: 'In Namecheap: Domain List › Manage › Advanced DNS.',
    url: 'https://ap.www.namecheap.com/domains/list/',
  },
  ionos: {
    name: 'IONOS',
    where: 'In IONOS: Domains & SSL › your domain › DNS.',
    url: 'https://my.ionos.com/domains',
  },
  other: {
    name: 'your provider',
    where: 'Sign in where you bought the domain and look for DNS, DNS records or Zone editor.',
    url: null,
  },
};
const PROVIDER_KEYS = Object.keys(PROVIDERS) as ProviderKey[];

const STEPS = ['Domain', 'Add records', 'Check', 'Live'];
const POLL_MS = 20_000;
const POLL_MAX = 30;

/** What someone types → a hostname: no scheme, path or trailing dot. */
const clean = (raw: string) =>
  raw
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/[/?#].*$/, '')
    .replace(/\.$/, '');
const VALID = /^([a-z0-9-]+\.)+[a-z]{2,}$/;
// Same as the server's rootOf: a country second level (example.co.uk) is three labels.
const SECOND_LEVEL = new Set(['co', 'com', 'org', 'net', 'ac', 'gov', 'edu', 'ltd', 'plc']);
const rootOf = (host: string) => {
  const l = host.split('.');
  const n =
    l.length > 2 && l[l.length - 1].length === 2 && SECOND_LEVEL.has(l[l.length - 2]) ? 3 : 2;
  return l.slice(-n).join('.');
};

const when = (iso?: string | null) => {
  if (!iso) return null;
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

/** The row's status pill. */
function pillOf(d: Domain): { label: string; tone: 'ok' | 'wait' | 'idle' | 'bad' } {
  if (d.paused) return { label: 'Paused · not on your plan', tone: 'bad' };
  if (d.status === 'active') return { label: 'Connected', tone: 'ok' };
  if (d.status === 'failed') return { label: 'Needs attention', tone: 'bad' };
  if (d.status === 'verifying') return { label: 'Setting up https', tone: 'wait' };
  return d.lastCheckedAt
    ? { label: 'Checking records', tone: 'wait' }
    : { label: 'Waiting for records', tone: 'idle' };
}

/** The steps as plain text, for whoever manages the domain. */
function stepsEmail(d: Domain, prov: ProviderKey) {
  const p = PROVIDERS[prov];
  const rows = d.records
    .map((r) => `${r.type}   Name: ${r.host || r.name}   Value: ${r.value}`)
    .join('\n');
  const body = `Hi,

Could you add these two DNS records for ${d.root || rootOf(d.hostname)}? They point ${d.hostname} at our website on tinycms.

${rows}

${p.where}
If a CNAME for ${d.records[0]?.host || d.hostname} already exists, edit it instead of adding a second one. Some providers call Name "Host" and Value "Points to" or "Target". Type the names exactly as shown: the provider adds ${d.root || rootOf(d.hostname)} on its own.

Thanks!`;
  return `mailto:?subject=${encodeURIComponent(`DNS records for ${d.hostname}`)}&body=${encodeURIComponent(body)}`;
}

export function DomainConnect() {
  const { accountId } = useAccount();
  const { copy, state: copied } = useCopy();
  const { data, loading, refetch } = useQuery(GET_TINYCMS_DOMAINS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const info = data?.tinycmsDomains;
  const domains: Domain[] = useMemo(() => info?.domains || [], [info]);
  const canManage = Boolean(info?.canManage);
  const allows: number | null = info?.planAllows ?? null;

  const [add, { loading: adding }] = useMutation(ADD_TINYCMS_DOMAIN, {
    refetchQueries: [SETTINGS_HISTORY_QUERY],
  });
  const [check, { loading: checking }] = useMutation(CHECK_TINYCMS_DOMAIN);
  const [setPrimary] = useMutation(SET_PRIMARY_TINYCMS_DOMAIN, {
    refetchQueries: [SETTINGS_HISTORY_QUERY],
  });
  const [remove] = useMutation(REMOVE_TINYCMS_DOMAIN, {
    refetchQueries: [SETTINGS_HISTORY_QUERY],
  });

  // The flow: which step, for which domain (its id once added).
  const [step, setStep] = useState(0);
  const [flowId, setFlowId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [tried, setTried] = useState(false);
  const [error, setError] = useState('');
  const [prov, setProv] = useState<ProviderKey | null>(null);
  const [checks, setChecks] = useState(0);
  const resumed = useRef(false);

  const flow = domains.find((d) => d.id === flowId) || null;
  // Back on the page with a domain half set up: pick up where it was.
  useEffect(() => {
    if (resumed.current || !info || flowId) return;
    resumed.current = true;
    const open = domains.find((d) => d.status !== 'active');
    if (open && canManage) {
      setFlowId(open.id);
      setStep(open.lastCheckedAt ? 3 : 2);
    }
  }, [info, domains, flowId, canManage]);

  const runCheck = async (id: string) => {
    try {
      await check({ variables: { accountId, id } });
      setChecks((n) => n + 1);
      await refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The check didn’t run. Try again.');
    }
  };

  // Step 3 checks on its own while the page is open; the engine keeps going after.
  useEffect(() => {
    if (step !== 3 || !flow || flow.status === 'active' || checks >= POLL_MAX) return undefined;
    const t = setTimeout(() => void runCheck(flow.id), POLL_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, flow?.id, flow?.status, flow?.lastCheckedAt, checks]);

  // Live: on to step 4, once.
  const announced = useRef<string | null>(null);
  useEffect(() => {
    if (step === 3 && flow?.status === 'active' && announced.current !== flow.id) {
      announced.current = flow.id;
      showInkToast(`${flow.hostname} is connected and secure.`);
      setStep(4);
    }
  }, [step, flow]);

  if (loading && !info) return <Loader label="Loading" className="ink-site-hint" />;

  const primary = domains.find((d) => d.isPrimary && d.status === 'active' && !d.paused);
  const atLimit = allows !== null && domains.length >= allows;
  const raw = clean(input);
  const valid = VALID.test(raw);
  const isRoot = valid && raw === rootOf(raw);

  const start = () => {
    setFlowId(null);
    setInput('');
    setTried(false);
    setError('');
    setProv(null);
    setChecks(0);
    setStep(1);
  };
  const close = () => {
    setStep(0);
    setFlowId(null);
    setError('');
  };

  const next1 = async () => {
    setTried(true);
    setError('');
    if (!valid || isRoot) return;
    // Same domain as before Back: carry on with it.
    if (flow && flow.hostname === raw) {
      setStep(2);
      return;
    }
    try {
      // Back, then a different domain: the first one's records are no use.
      if (flow && flow.status !== 'active') {
        await remove({ variables: { accountId, id: flow.id } });
      }
      const { data: res } = await add({ variables: { accountId, hostname: raw } });
      const made = res?.addTinycmsDomain;
      await refetch();
      setFlowId(made?.id || null);
      setProv(null);
      setChecks(0);
      setStep(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That domain couldn’t be added.');
    }
  };

  const removeDomain = async (d: Domain) => {
    const where = PROVIDERS[(d.provider as ProviderKey) || 'other'].name;
    const ok = await askInk({
      title: `Remove ${d.hostname}?`,
      body:
        d.status === 'active'
          ? 'It stops showing your site. Readers who go there see nothing.'
          : 'Its setup stops here.',
      note: `Afterwards you can delete the two records at ${where}.`,
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      await remove({ variables: { accountId, id: d.id } });
      if (flowId === d.id) close();
      await refetch();
      showInkToast(`${d.hostname} removed.`);
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    }
  };

  const togglePrimary = async (d: Domain) => {
    try {
      await setPrimary({ variables: { accountId, id: d.isPrimary ? null : d.id } });
      await refetch();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t save.');
    }
  };

  const provKey: ProviderKey = prov || (flow?.provider as ProviderKey) || 'other';
  const p = PROVIDERS[provKey];
  const root = flow?.root || (flow ? rootOf(flow.hostname) : '');
  const [cname, txt] = flow?.records || [];
  const cnameOk = Boolean(cname?.ok) || flow?.status === 'active';
  const txtOk = Boolean(txt?.ok) || flow?.status === 'verifying' || flow?.status === 'active';
  const dnsDone = cnameOk && txtOk;
  // After a couple of checks with something still missing, say what to look at.
  const slow = step === 3 && checks >= 2 && !txtOk;
  const copyLabel = (key: string) =>
    copied(key) === 'copied' ? 'Copied' : copied(key) === 'failed' ? 'Couldn’t copy' : 'Copy';

  return (
    <div className="dm">
      <div className="dm-row">
        <span className="dm-host">{info?.freeAddress || 'Set a handle first'}</span>
        {info?.freeAddress ? (
          <span className="ink-site-pill">
            {primary ? `Sends visitors to ${primary.hostname}` : 'Primary · free'}
          </span>
        ) : null}
        {info?.freeAddress ? (
          <button
            type="button"
            className="ink-site-link dm-end"
            onClick={(e) => void copy('free', info.freeAddress, e)}
          >
            {copyLabel('free')}
          </button>
        ) : null}
      </div>

      {domains
        .filter((d) => !(step === 1 && d.id === flowId))
        .map((d) => {
          const pill = pillOf(d);
          return (
            <div key={d.id} className="dm-row">
              <span className="dm-host">{d.hostname}</span>
              <span className="dm-pill" data-tone={pill.tone}>
                <span className="dm-pill-dot" />
                {pill.label}
              </span>
              {d.status === 'active' && d.isPrimary && !d.paused ? (
                <span className="ink-site-pill">Main address</span>
              ) : null}
              {canManage ? (
                <span className="dm-end dm-row-actions">
                  {d.status !== 'active' && flowId !== d.id ? (
                    <button
                      type="button"
                      className="ink-site-link"
                      onClick={() => {
                        setFlowId(d.id);
                        setChecks(0);
                        setStep(d.lastCheckedAt ? 3 : 2);
                      }}
                    >
                      Finish setup
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="ink-site-link dm-remove"
                    onClick={() => void removeDomain(d)}
                  >
                    Remove
                  </button>
                </span>
              ) : null}
            </div>
          );
        })}

      {domains.some((d) => d.paused) ? (
        <p className="ink-bill-note dm-pad" data-tone="warn">
          Your plan includes {allows === 0 ? 'no' : allows} custom web address
          {allows === 1 ? '' : 'es'}, so the rest are paused: visitors there see a “paused” page.
          Nothing was removed. <Link to="/settings/billing">Change plan</Link> to turn them back on.
        </p>
      ) : null}

      {step === 0 ? (
        !canManage ? (
          <p className="ink-site-hint dm-pad">Only an account admin can change domains.</p>
        ) : allows === 0 ? (
          <div className="dm-idle">
            <div className="dm-idle-text">
              Have a domain like example.com? Custom domains come with a paid plan.
            </div>
            <Link to="/settings/billing" className="ink-btn ink-btn-primary">
              See plans
            </Link>
          </div>
        ) : !domains.length ? (
          <div className="dm-idle">
            <div className="dm-idle-text">
              Have a domain like example.com? Point it at this site in about 10 minutes. You add two
              records where you bought the domain; we handle the rest.
            </div>
            <button type="button" className="ink-btn ink-btn-primary" onClick={start}>
              Connect a domain
            </button>
          </div>
        ) : !atLimit ? (
          <div className="dm-idle">
            <button type="button" className="ink-site-link" onClick={start}>
              Connect another domain
            </button>
          </div>
        ) : null
      ) : (
        <div className="dm-flow">
          <ol className="dm-steps" aria-label="Steps">
            {STEPS.map((label, i) => (
              <li
                key={label}
                aria-current={i + 1 === step ? 'step' : undefined}
                data-done={i + 1 < step ? 'true' : undefined}
                data-on={i + 1 <= step ? 'true' : undefined}
              >
                <span className="dm-step-bar" />
                <span className="dm-step-label">
                  {i + 1}. {label}
                </span>
              </li>
            ))}
          </ol>

          {step === 1 ? (
            <div className="dm-stack">
              <div>
                <div className="dm-h">Which domain do you want to use?</div>
                <div className="dm-p">
                  Type the address you want readers to visit. You need to own it already.
                </div>
              </div>
              <label className="dm-label">
                Domain
                <input
                  className="ink-site-input dm-mono"
                  placeholder="www.example.com"
                  value={input}
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                  aria-invalid={(tried && !valid) || Boolean(error) || undefined}
                  aria-describedby="dm-domain-help"
                  data-bad={(tried && !valid) || error ? 'true' : undefined}
                  onChange={(e) => {
                    setInput(e.target.value);
                    setTried(false);
                    setError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void next1();
                  }}
                />
                <span
                  id="dm-domain-help"
                  className="dm-help"
                  data-bad={(tried && !valid) || error ? 'true' : undefined}
                >
                  {error ||
                    (tried && !valid
                      ? 'Type a domain like www.example.com, without https:// or a slash'
                      : 'Usually www.yourdomain.com, or a part of it like blog.yourdomain.com')}
                </span>
              </label>
              {isRoot ? (
                <div className="dm-note">
                  <span>
                    {raw} on its own can’t point at a website yet. Most sites use www.{raw}, and
                    your provider can forward {raw} to it.
                  </span>
                  <button
                    type="button"
                    className="ink-site-link"
                    onClick={() => {
                      setInput(`www.${raw}`);
                      setTried(false);
                    }}
                  >
                    Use www.{raw}
                  </button>
                </div>
              ) : null}
              <div className="dm-actions">
                <button type="button" className="ink-btn" onClick={close}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="ink-btn ink-btn-primary"
                  onClick={() => void next1()}
                  disabled={adding || (tried && isRoot)}
                >
                  {adding ? 'Adding…' : 'Continue'}
                </button>
              </div>
            </div>
          ) : null}

          {step === 2 && flow ? (
            <div className="dm-stack">
              <div>
                <div className="dm-h">Add two records at {p.name}</div>
                <div className="dm-p">
                  Records tell the internet that {flow.hostname} should show this site. They live
                  where you bought or manage the domain, not in tinycms.
                </div>
              </div>
              <div className="dm-prov">
                <span>
                  {prov
                    ? `You picked ${p.name}.`
                    : flow.provider
                      ? `It looks like ${root} is managed at ${p.name}.`
                      : `We couldn’t tell where ${root} is managed.`}
                </span>
                <label className="dm-prov-pick">
                  <span>Provider</span>
                  <select
                    className="ink-site-input dm-select"
                    value={provKey}
                    aria-label="Domain provider"
                    onChange={(e) => setProv(e.target.value as ProviderKey)}
                  >
                    {PROVIDER_KEYS.map((k) => (
                      <option key={k} value={k}>
                        {k === 'other' ? 'Somewhere else' : PROVIDERS[k].name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <ol className="dm-howto">
                <li>
                  <span className="dm-num">1</span>
                  <div>
                    <span className="dm-li-h">Open the DNS settings for {root}</span>
                    <span className="dm-p">{p.where}</span>
                    {p.url ? (
                      <a className="ink-site-link" href={p.url} target="_blank" rel="noreferrer">
                        Open {p.name} in a new tab ↗
                      </a>
                    ) : null}
                  </div>
                </li>
                <li>
                  <span className="dm-num">2</span>
                  <div>
                    <span className="dm-li-h">Add these two records, exactly as shown</span>
                    <div className="dm-table" role="table" aria-label="DNS records">
                      <div className="dm-tr dm-th" role="row">
                        <span role="columnheader">Type</span>
                        <span role="columnheader">Name (host)</span>
                        <span role="columnheader">Value (points to)</span>
                      </div>
                      {flow.records.map((r) => {
                        const host = r.host || r.name;
                        return (
                          <div key={r.type} className="dm-tr" role="row">
                            <span role="cell" className="dm-type">
                              {r.type}
                            </span>
                            <span role="cell" className="dm-cell">
                              <span className="dm-mono">{host}</span>
                              <button
                                type="button"
                                className="dm-copy"
                                aria-label={`Copy name ${host}`}
                                onClick={(e) => void copy(`${r.type}:name`, host, e)}
                              >
                                {copyLabel(`${r.type}:name`)}
                              </button>
                            </span>
                            <span role="cell" className="dm-cell">
                              <span className="dm-mono">{r.value}</span>
                              <button
                                type="button"
                                className="dm-copy"
                                aria-label={`Copy value for ${r.type}`}
                                onClick={(e) => void copy(`${r.type}:value`, r.value, e)}
                              >
                                {copyLabel(`${r.type}:value`)}
                              </button>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <span className="dm-p dm-small">
                      Already have a {cname?.host && cname.host !== '@' ? `${cname.host} ` : ''}
                      CNAME? Edit it instead of adding a second one. Some providers call Name
                      “Host”, and Value “Points to” or “Target”.
                    </span>
                  </div>
                </li>
                <li>
                  <span className="dm-num">3</span>
                  <div>
                    <span className="dm-li-h">Save them, then come back here</span>
                    <span className="dm-p">
                      Click the button below and we’ll start checking. You can close this page; we
                      keep checking and email you.
                    </span>
                  </div>
                </li>
              </ol>

              <div className="dm-actions">
                <button
                  type="button"
                  className="ink-btn"
                  onClick={() => {
                    setInput(flow.hostname);
                    setTried(false);
                    setStep(1);
                  }}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="ink-btn ink-btn-primary"
                  onClick={() => {
                    setChecks(0);
                    setStep(3);
                    void runCheck(flow.id);
                  }}
                >
                  I’ve added the records
                </button>
                <a className="ink-site-link dm-mail" href={stepsEmail(flow, provKey)}>
                  Send these steps to someone else
                </a>
              </div>
            </div>
          ) : null}

          {step === 3 && flow ? (
            <div className="dm-stack">
              <div>
                <div className="dm-h">
                  {dnsDone ? 'Records found. Setting up https…' : 'Checking for your records'}
                </div>
                <div className="dm-p">
                  {dnsDone
                    ? 'This takes a few minutes. Nothing for you to do.'
                    : 'Changes can take a few minutes to show up, and now and then up to 48 hours. You can leave this page. We keep checking and email you when it works.'}
                </div>
              </div>
              <div className="dm-checks">
                {[
                  {
                    ok: cnameOk,
                    label: 'CNAME record',
                    sub: `${cname?.host || flow.hostname} → ${cname?.value || ''}`,
                    wait: checking ? 'Checking…' : 'Not found yet',
                  },
                  {
                    ok: txtOk,
                    label: 'TXT record',
                    sub: `${txt?.host || ''} proves you own ${root}`,
                    wait: checking ? 'Checking…' : 'Not found yet',
                  },
                  {
                    ok: flow.status === 'active',
                    label: 'Secure connection (https)',
                    sub: 'We set this up for you once both records are found',
                    wait: dnsDone ? 'Setting up…' : 'After records',
                    done: 'Ready',
                    plain: true,
                  },
                ].map((c) => (
                  <div key={c.label} className="dm-check" data-ok={c.ok ? 'true' : undefined}>
                    <span className="dm-check-dot" aria-hidden="true">
                      {c.ok ? '✓' : '•'}
                    </span>
                    <div className="dm-check-text">
                      <div className="dm-check-label">{c.label}</div>
                      <div className="dm-check-sub" data-plain={c.plain ? 'true' : undefined}>
                        {c.sub}
                      </div>
                    </div>
                    <span className="dm-check-st">{c.ok ? c.done || 'Found' : c.wait}</span>
                  </div>
                ))}
              </div>
              {flow.lastError && (slow || flow.status === 'failed') ? (
                <div className="dm-note" data-tone="warn">
                  <span>{flow.lastError}.</span>
                </div>
              ) : slow ? (
                <div className="dm-note" data-tone="warn">
                  <span>
                    Still not seeing the TXT record. Check its name is exactly{' '}
                    <b className="dm-mono">{txt?.host}</b>. Some providers add {root} on their own,
                    so typing it in again doubles it.
                  </span>
                </div>
              ) : null}
              <div className="dm-actions">
                {flow.status !== 'active' ? (
                  <>
                    <button type="button" className="ink-btn" onClick={() => setStep(2)}>
                      See the records again
                    </button>
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary"
                      disabled={checking}
                      onClick={() => void runCheck(flow.id)}
                    >
                      {checking ? 'Checking…' : 'Check now'}
                    </button>
                  </>
                ) : null}
                <span className="dm-last">
                  {checking
                    ? 'Checking now'
                    : flow.lastCheckedAt
                      ? `Last checked ${when(flow.lastCheckedAt)} · we keep checking every 5 minutes`
                      : 'We keep checking every 5 minutes'}
                </span>
              </div>
            </div>
          ) : null}

          {step === 4 && flow ? (
            <div className="dm-stack">
              <div>
                <div className="dm-h">{flow.hostname} is live</div>
                <div className="dm-p">
                  It’s connected and secure (https). Readers can visit it now.
                </div>
              </div>
              <label className="dm-primary">
                <input
                  type="checkbox"
                  checked={flow.isPrimary}
                  onChange={() => void togglePrimary(flow)}
                />
                <span>
                  <span className="dm-li-h">Make it the main address</span>
                  <span className="dm-p">
                    Links, sharing and search results use {flow.hostname}.
                    {info?.freeAddress
                      ? ` Visitors to ${info.freeAddress} are sent there automatically.`
                      : ''}
                  </span>
                </span>
              </label>
              <div className="dm-actions">
                <a
                  className="ink-btn"
                  href={`https://${flow.hostname}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Visit {flow.hostname} ↗
                </a>
                <button
                  type="button"
                  className="ink-btn ink-btn-primary"
                  onClick={() => {
                    showInkToast(
                      `${flow.hostname}${flow.isPrimary ? ' is now the main address.' : ' is connected.'}`
                    );
                    close();
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
